import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';

// A rejected request reverts cancellation_status to 'None', so it's told apart
// from "never requested" by having a cancellation_processed_at timestamp.
const cancellationState = (sub) => {
    if (sub.cancellation_status === 'Requested') return 'Pending';
    if (sub.cancellation_status === 'Cancelled') return 'Approved';
    if (sub.cancellation_status === 'None' && sub.cancellation_processed_at) return 'Rejected';
    return 'None';
};

const stateBadge = (state) => {
    if (state === 'Approved') return 'badge-light-success';
    if (state === 'Rejected') return 'badge-light-danger';
    if (state === 'Pending') return 'badge-light-warning';
    return 'badge-light-secondary';
};

const money = (value) => `AED ${Number(value || 0).toFixed(2)}`;

const CancellationRequests = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const planFilter = searchParams.get('plan_id') || '';
    const stateFilter = searchParams.get('state') || 'Requested';

    const [isLoader, setIsLoader] = useState(false);
    const [requests, setRequests] = useState([]);
    const [plans, setPlans] = useState([]);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);

    useEffect(() => {
        getPlansList();
    }, []);

    useEffect(() => {
        getRequests();
    }, [page, planFilter, stateFilter]);

    const getPlansList = async () => {
        try {
            const response = await ApiService.request({
                method: "GET",
                url: "admin/membership-plans-list",
            });
            if (response.data.status) setPlans(response.data.data);
        } catch (error) {
            console.error(error);
        }
    };

    const getRequests = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/membership-plans/subscribers",
                data: {
                    page,
                    cancellation_status: stateFilter,
                    ...(planFilter && { plan_id: planFilter }),
                },
            });
            const data = response.data;
            if (data.status) {
                setRequests(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
    };

    const applyFilters = (next) => {
        setPage(1);
        const merged = { plan_id: planFilter, state: stateFilter, ...next };
        const params = {};
        if (merged.plan_id) params.plan_id = merged.plan_id;
        if (merged.state) params.state = merged.state;
        setSearchParams(params);
    };

    const updateCancellationApproval = async (sub, approval_status) => {
        const isApprove = approval_status === 'Approved';
        const refund = Number(sub.refund_amount) || 0;

        const result = await Swal.fire({
            title: isApprove ? 'Approve Cancellation?' : 'Reject Cancellation Request',
            html: isApprove
                ? `This will cancel the membership${refund > 0 ? ` and refund <b>${money(refund)}</b> to the member's original payment method via Stripe.` : ', no refund is due.'}`
                : 'The membership stays active. This is shown to the member.',
            icon: 'warning',
            input: isApprove ? undefined : 'textarea',
            inputLabel: isApprove ? undefined : 'Reason for rejection',
            inputPlaceholder: isApprove ? undefined : 'This is shown to the member…',
            inputAttributes: isApprove ? undefined : { maxlength: 300 },
            inputValidator: isApprove
                ? undefined
                : (value) => (!value || !value.trim() ? 'A rejection reason is required' : undefined),
            showCancelButton: true,
            confirmButtonColor: isApprove ? '#4BC95F' : '#d33',
            confirmButtonText: isApprove ? 'Yes, approve' : 'Yes, reject',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            try {
                const response = await ApiService.request({
                    method: "POST",
                    url: `admin/membership-plans/subscribers/${sub._id}/cancellation-approval`,
                    data: {
                        approval_status,
                        ...(!isApprove && { rejection_reason: result.value.trim() }),
                    },
                });
                const data = response.data;
                if (data.status) {
                    getRequests();
                    toast.success(data.message);
                } else {
                    toast.error(data.message);
                }
            } catch (error) {
                toast.error('Something went wrong.');
            }
        }
    };

    // Pending approvals hold an authorised payment, so the server refuses to
    // delete them until they're approved or rejected.
    const deleteMembership = async (sub) => {
        const result = await Swal.fire({
            title: 'Delete this membership?',
            text: `This permanently removes ${sub.user_id?.name ? `${sub.user_id.name}'s` : 'this'} ${sub.plan_id?.title || 'membership'} record. No payment or refund is processed.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });
        if (!result.isConfirmed) return;

        try {
            const response = await ApiService.request({
                method: "DELETE",
                url: `admin/membership-plans/subscribers/${sub._id}`,
            });
            const data = response.data;
            if (data.status) {
                getRequests();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Something went wrong.');
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Cancellation Requests</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/membership-plans" className="text-muted text-hover-primary">Membership Plans</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Cancellation Requests</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <div className="row">
                                    <div className="col-md-4">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={planFilter}
                                            onChange={(e) => applyFilters({ plan_id: e.target.value })}
                                        >
                                            <option value="">All Plans</option>
                                            {plans.map((plan) => (
                                                <option key={plan._id} value={plan._id}>{plan.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={stateFilter}
                                            onChange={(e) => applyFilters({ state: e.target.value })}
                                        >
                                            <option value="Requested">Pending Review</option>
                                            <option value="Cancelled">Approved</option>
                                            <option value="Rejected">Rejected</option>
                                            <option value="Any">All Cancellation Activity</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-0">
                            <div className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>Member</th>
                                                <th>Contact</th>
                                                <th>Plan</th>
                                                <th>Requested On</th>
                                                <th>Sessions Used</th>
                                                <th>Charge</th>
                                                <th>Refund</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {requests.length > 0 ? requests.map((sub) => {
                                                const state = cancellationState(sub);
                                                return (
                                                    <tr key={sub._id}>
                                                        <td>
                                                            {sub.user_id ? (
                                                                <Link to={`/admin/users/${sub.user_id._id}`} className="text-dark fw-bold text-hover-primary">
                                                                    {sub.user_id.name || '—'}
                                                                </Link>
                                                            ) : <span className="text-muted">Deleted user</span>}
                                                        </td>
                                                        <td>
                                                            <div>{sub.user_id?.email || '—'}</div>
                                                            <div className="text-muted fs-7">{sub.user_id?.phone || ''}</div>
                                                        </td>
                                                        <td>{sub.plan_id?.title || '—'}</td>
                                                        <td>{sub.cancellation_requested_at ? moment(sub.cancellation_requested_at).format('DD MMM YYYY') : '—'}</td>
                                                        <td>{sub.sessions_used_at_cancellation ?? '—'}</td>
                                                        <td>{money(sub.cancellation_charge)}</td>
                                                        <td className="text-success fw-bold">{money(sub.refund_amount)}</td>
                                                        <td>
                                                            <span className={`badge ${stateBadge(state)}`}>{state}</span>
                                                            {state === 'Rejected' && sub.cancellation_rejection_reason && (
                                                                <div className="text-muted fs-8 mt-1" style={{ maxWidth: 220 }}>
                                                                    {sub.cancellation_rejection_reason}
                                                                </div>
                                                            )}
                                                            {state === 'Pending' && sub.cancellation_reason && (
                                                                <div className="text-muted fs-8 mt-1" style={{ maxWidth: 220 }}>
                                                                    Reason: {sub.cancellation_reason}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="mt-2">
                                                            {state === 'Pending' ? (
                                                                <>
                                                                    <button
                                                                        onClick={() => updateCancellationApproval(sub, 'Approved')}
                                                                        className="btn btn-sm h-35px btn-light-success"
                                                                        title="Approve"
                                                                    >
                                                                        <i className="bi bi-check-lg fs-6"></i>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => updateCancellationApproval(sub, 'Rejected')}
                                                                        className="btn btn-sm h-35px btn-light-danger ms-2"
                                                                        title="Reject"
                                                                    >
                                                                        <i className="bi bi-x-lg fs-6"></i>
                                                                    </button>
                                                                </>
                                                            ) : null}
                                                            <button
                                                                onClick={() => deleteMembership(sub)}
                                                                className={`btn btn-sm h-35px btn-light-danger${state === 'Pending' ? ' ms-2' : ''}`}
                                                                title="Delete"
                                                            >
                                                                <i className="bi bi-trash fs-6"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr>
                                                    <td colSpan="9" className="text-dark text-center mt-2">No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center" />
                                    )}

                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={setPage}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CancellationRequests;
